import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { naviguer } from '../../../App';
import type { Demande } from '../../../types';
import { ApercuDemande } from './ApercuDemande';
import { ConfirmationEnvoi } from './ConfirmationEnvoi';
import { jourLong, moisSansAnnee, type DateIso } from './dates';
import { FenetreSeance } from './FenetreSeance';
import { FenetreSolde } from './FenetreSolde';
import { GrilleSelection } from './GrilleSelection';
import { PanneauDemande } from './PanneauDemande';
import { IconeEtat } from './PastilleSeance';
import type { Seance } from './seancesDemande';
import { useSurvol, useTexteSurvole, type Survol } from './survol';
import { dePersonne, LIBELLE_ETAT } from './textesDemande';
import { useNouvelleDemande, type PrefillNouvelleDemande } from './useNouvelleDemande';

// Les consignes tiennent sur UNE ligne (au moins jusqu'à ~600 px de large pour le calendrier) : courtes, sans second membre de phrase.
const VOUS_CONSIGNE = "Cliquez ou glissez sur vos jours d'absence, puis cliquez une séance pour indiquer un·e remplaçant·e.";
const HINT_DEFAUT = "Glissez pour choisir une période · Maj (⇧) + clic pour l'étendre, même sur un autre mois.";
const AIDE_GRILLE = "Flèches : changer de jour. Début, Fin : premier, dernier jour de la semaine. Page précédente, Page suivante : mois précédent, mois suivant. Espace : choisir ou retirer le jour. Maj + Espace : choisir tous les jours depuis le dernier jour choisi, même dans un autre mois. Maj + flèches : étendre la sélection.";

/**
 * La ligne d'aide sous la légende : l'explication du jour survolé, sinon le message du dernier geste. Seule elle se redessine quand on survole. UNE ligne, jamais
 * deux (un texte trop long est coupé par « … », en entier dans l'info-bulle et pour les lecteurs d'écran) : la ligne ne change donc jamais de hauteur et le
 * calendrier ne bouge pas sous la souris.
 */
function LigneAide({ survol, parDefaut }: { survol: Survol; parDefaut: string }) {
  const texte = useTexteSurvole(survol) ?? parDefaut;
  return <p className="nd-hint" aria-hidden="true" title={texte}>{texte}</p>;
}

/** « Nouvelle demande » : le calendrier du mois à gauche, le panneau « Ma demande » à droite qui se remplit en direct (DECISIONS §26). */
export function PageNouvelleDemande({ prefill }: { prefill?: PrefillNouvelleDemande }) {
  const nd = useNouvelleDemande(prefill);
  const { etat, aujourdhui, seancesChoisies } = nd;
  const [fenetre, setFenetre] = useState<{ cle: string; ou: 'c' | 'p'; garant?: boolean } | null>(null);
  const [apercu, setApercu] = useState<{ seance: Seance | null; demande: Demande | null } | null>(null);
  const survol = useSurvol();
  const racine = useRef<HTMLDivElement>(null);
  const [etroit, setEtroit] = useState(false);

  // La page prend toute la hauteur sous la barre du haut (et sous le bandeau d'aperçu administrateur) : on mesure où elle commence.
  useLayoutEffect(() => {
    const mesurer = () => { if (racine.current) racine.current.style.setProperty('--nd-decalage', `${Math.round(racine.current.getBoundingClientRect().top + window.scrollY)}px`); };
    mesurer();
    window.addEventListener('resize', mesurer);
    // Sous ~940 px de large utile (zoom, fenêtre étroite, menu ouvert) : une seule colonne, le panneau sous le calendrier, la page défile.
    const ro = new ResizeObserver(() => setEtroit((racine.current?.clientWidth ?? 9999) < 940));
    if (racine.current) ro.observe(racine.current);
    return () => { window.removeEventListener('resize', mesurer); ro.disconnect(); };
  }, []);

  const fermerFenetre = useCallback(() => setFenetre(null), []);
  /** « Nouvelle demande » après l'envoi : page vide, et le focus au début du formulaire (la personne à choisir pour la gestion, sinon le calendrier). */
  const nouvelleDemande = useCallback(() => {
    nd.reinitialiser();
    nd.apresRendu(() => {
      document.getElementById('nd-panneau-corps')?.scrollTo({ top: 0 });   // le panneau revient en haut : le titre et le premier champ sont sous les yeux
      document.querySelector<HTMLElement>(nd.peutChoisirAutrui ? '#nd-absent' : '.nd-jour[tabindex="0"]')?.focus();   // sans « preventScroll » : en une colonne la page remonte jusqu'au champ
    });
  }, [nd]);
  const onOuvrirSeance = useCallback((s: Seance, ou: 'c' | 'p') => {
    if (s.existante || s.priseEnCharge) { setFenetre(null); setApercu({ seance: s, demande: s.existante }); return; }
    setFenetre((f) => (f && f.cle === s.cle && f.ou === ou ? null : { cle: s.cle, ou }));
  }, []);
  const onOuvrirParCle = useCallback((cle: string) => setFenetre({ cle, ou: 'p', garant: true }), []);
  const onVoirJour = useCallback((d: DateIso) => {
    const n = document.querySelector<HTMLElement>(`[data-jour-panneau="${d}"]`);
    if (!n) return;
    n.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    n.focus({ preventScroll: true });
  }, []);

  const seanceOuverte = fenetre ? seancesChoisies.find((s) => s.cle === fenetre.cle) ?? null : null;
  // La séance a disparu (jour retiré, personne absente changée) : la fenêtre se ferme.
  useEffect(() => { if (fenetre && !seanceOuverte) setFenetre(null); }, [fenetre, seanceOuverte]);

  // Une personne intervenante demande toujours pour elle-même : jamais « choisissez la personne absente » (même avant que sa fiche soit arrivée).
  const sansFiche = !nd.peutChoisirAutrui && !nd.maPersonne && !nd.chargementMoi;   // compte non relié à une fiche (ou « Aperçu intervenant·e » sans fiche)
  const consigne = sansFiche
    ? "Votre compte n'est relié à aucune fiche intervenant·e : contactez le secrétariat pour pouvoir créer une demande."
    : nd.peutChoisirAutrui && etat.absentId == null
    ? "Choisissez d'abord la personne absente (panneau de droite)."
    : nd.dire(VOUS_CONSIGNE, `Cliquez ou glissez sur les jours d'absence ${dePersonne(nd.voix.prenom)}, puis cliquez une séance pour indiquer un·e remplaçant·e.`);

  return (
    <div className={`nd${etroit ? ' nd--etroit' : ''}`} ref={racine}>
      <section className="nd-cal" aria-labelledby="nd-titre">
        <div className="nd-cal-tete">
          <div>
            <h1 className="page-title" id="nd-titre">Nouvelle demande</h1>
            <p className="page-meta">{consigne}</p>
            <button
              type="button" className="btn btn-sm nd-saut"
              onClick={() => { const t = document.getElementById('nd-panneau-titre'); t?.scrollIntoView({ block: 'start' }); t?.focus({ preventScroll: true }); }}
            >Aller au panneau de la demande</button>
          </div>
        </div>

        <div className="nd-legende" aria-hidden="true">
          {(['rech', 'trouve', 'deja', 'off'] as const).map((k) => <span key={k} className={`nd-leg nd-st-${k}`}><IconeEtat etat={k} />{LIBELLE_ETAT[k]}</span>)}
        </div>
        <LigneAide survol={survol} parDefaut={etat.message || HINT_DEFAUT} />
        <p className="nd-sr" role="status">{etat.message}{etat.repete % 2 ? '\u00a0' : ''}</p>
        <p className="nd-sr" id="nd-grille-aide">{AIDE_GRILLE}</p>

        {nd.erreurChargement && <p className="nd-alerte" role="alert">{nd.erreurChargement}</p>}
        {nd.moisEnErreur.length > 0 && (
          <p className="nd-alerte" role="alert">
            Impossible de charger les séances de {nd.moisEnErreur.map((m) => moisSansAnnee(`${m}-01`, aujourdhui)).join(', ')}.{' '}
            <button type="button" className="nd-lien nd-lien--ligne" onClick={nd.rechargerProgramme}>Réessayer</button>
          </p>
        )}
        {nd.echec.demandes && (
          <p className="nd-alerte" role="alert">
            {nd.dire('Vos demandes déjà faites', 'Les demandes déjà faites')} n'ont pas pu être vérifiées : « Envoyer » reste inactif.{' '}
            <button type="button" className="nd-lien nd-lien--ligne" onClick={nd.rechargerDonnees}>Réessayer</button>
          </p>
        )}
        {nd.echec.absences && !nd.echec.demandes && (
          <p className="nd-alerte" role="alert">
            Les absences de vos collègues n'ont pas pu être chargées : vérifiez leur disponibilité avant de choisir.{' '}
            <button type="button" className="nd-lien nd-lien--ligne" onClick={nd.rechargerDonnees}>Réessayer</button>
          </p>
        )}
        {nd.aucuneNouvelle && (
          <div className="nd-alerte" role="alert">
            <p>Toutes ces séances étaient déjà demandées : rien n'a été créé.</p>
            {nd.aucuneNouvelle.doublons.map((d) => (d.acces === 'prise_en_charge'
              ? null
              : <button key={d.id} type="button" className="btn btn-sm" onClick={() => setApercu({ seance: null, demande: d })}>{d.atelier_nom} · {jourLong(String(d.date_atelier).slice(0, 10))}</button>))}
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => nd.setAucuneNouvelle(null)}>Fermer</button>
          </div>
        )}

        {/* Le sélecteur de mois est rendu PAR le calendrier, juste avant sa grille : rien ne peut s'intercaler (voir BarreMois). */}
        <GrilleSelection nd={nd} ouverte={fenetre?.cle ?? null} onOuvrirSeance={onOuvrirSeance} onVoirJour={onVoirJour} onSurvol={survol.ecrire} />
      </section>

      <PanneauDemande nd={nd} ouverte={fenetre?.cle ?? null} onOuvrirSeance={onOuvrirSeance} onOuvrirParCle={onOuvrirParCle} onEnvoyer={() => { void nd.envoyer(false); }} />

      {fenetre && seanceOuverte && <FenetreSeance key={`${fenetre.ou}:${fenetre.cle}`} nd={nd} seance={seanceOuverte} ou={fenetre.ou} versGarant={fenetre.garant} onFermer={fermerFenetre} />}

      {nd.avertSolde && (
        <FenetreSolde controle={nd.avertSolde} voix={nd.voix} envoi={nd.envoi} onRevenir={() => nd.setAvertSolde(null)} onEnvoyerQuandMeme={() => { void nd.envoyer(true); }} />
      )}

      {nd.resultat && (
        <ConfirmationEnvoi
          resultat={nd.resultat} voix={nd.voix} libelleStatut={(s) => nd.meta?.statuts[s]} parDessus={apercu != null}
          onApercu={(d) => setApercu({ seance: null, demande: d })} onNouvelle={nouvelleDemande} onTableauDeBord={() => naviguer('tableau')}
        />
      )}

      {apercu && (
        <ApercuDemande
          seance={apercu.seance} demande={apercu.demande} libelleStatut={apercu.demande ? nd.meta?.statuts[apercu.demande.statut] : undefined} pourMoi={nd.pourMoi}
          onFermer={() => setApercu(null)} onChange={nd.rechargerDonnees}
        />
      )}
    </div>
  );
}
